<script setup lang="ts">
/*
* Компонент UsefulLinks - интерфейс для работы с полезными ссылками
*
* Основные функции:
* - Отображение списка полезных ссылок с пагинацией и бесконечной подгрузкой
* - Поиск и фильтрация ссылок по тексту
* - Управление избранными ссылками (добавление/удаление)
* - Разделение на вкладки "Все" и "Избранные"
*
* Особенности:
* - Реализация бесконечного скролла через Intersection Observer
* - Дебаунс для поиска для оптимизации запросов
* - Автоматическая подгрузка данных при прокрутке
* - Адаптивный дизайн для мобильных устройств
*/

// Импорты Vue и Vuetify
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify/framework'

// Импорты хранилищ
import { useLinksStore } from '@/modules/links/stores/linksStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'

// Импорты компонентов
import LinkCard from '@/modules/links/components/LinkCard.vue'
import { ERouteNames } from '@/router/ERouteNames'

// Инициализация хранилищ
const linksStore = useLinksStore()
const permissionsStore = usePermissionsStore()
const router = useRouter()

// Инициализация утилит отображения Vuetify
const { xs } = useDisplay()

// Реактивные состояния компонента
// Активная вкладка (0 - Все, 1 - Избранные)
const formTabValue = ref<number>(0)

// Локальное значение поискового запроса
const localSearch = ref<string>('')

// Референс на элемент-наблюдатель для бесконечного скролла
const sentinel = ref<HTMLElement | null>(null)

// Переменные для управления логикой
// Обсервер для отслеживания видимости элемента
let observer: IntersectionObserver | null = null

// Флаг выполнения загрузки данных
let isLoading = false

// Таймер для дебаунса поиска
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// Время задержки для дебаунса (мс)
const DEBOUNCE_MS = 400

/**
 * Обработчик переключения ссылки в избранное/из избранного
 *
 * Назначение:
 * - Выполняет API-запрос для изменения статуса избранного у ссылки
 * - Обновляет локальное состояние после успешного выполнения
 *
 * Входные данные:
 * - id: string - идентификатор ссылки для переключения
 */
async function onToggleFavorite(id: string) {
  await linksStore.toggleFavorite(id)
}

/**
 * Отключение Intersection Observer
 *
 * Назначение:
 * - Корректно отключает наблюдатель при смене вкладки или размонтировании
 * - Освобождает ресурсы и предотвращает утечки памяти
 *
 * Действия:
 * - Отключает наблюдение за элементами
 * - Сбрасывает ссылку на observer
 */
function disconnectObserver() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

/**
 * Настройка Intersection Observer для бесконечного скролла
 *
 * Назначение:
 * - Создает и настраивает наблюдатель для автоматической подгрузки данных
 * - Отслеживает появление sentinel-элемента в области видимости
 * - Загружает следующую порцию данных при необходимости
 *
 * Условия работы:
 * - Активна вкладка "Все" (formTabValue === 0)
 * - Имеется курсор для следующей страницы (nextUsefulLinksCursor)
 * - Sentinel-элемент присутствует в DOM
 *
 * Параметры наблюдателя:
 * - rootMargin: '150px' - предзагрузка за 150px до появления элемента
 * - threshold: 0.1 - срабатывает при 10% видимости элемента
 */
function setupObserver() {
  // не создаём observer если не в табе "Все" или нет курсора
  if (formTabValue.value !== 0 || !linksStore.nextUsefulLinksCursor) return
  const el = sentinel.value
  if (!el) return

  // отключаем старый, если есть
  if (observer) {
    observer.disconnect(); observer = null
  }

  observer = new IntersectionObserver(async entries => {
    const entry = entries[0]
    if (entry?.isIntersecting && !isLoading && linksStore.nextUsefulLinksCursor) {
      isLoading = true
      try {
        await linksStore.fetchUsefulLinks(linksStore.nextUsefulLinksCursor)
      } finally {
        isLoading = false
        // отключаем если больше нет курсора
        if (!linksStore.nextUsefulLinksCursor && observer) {
          observer.disconnect()
          observer = null
        }
      }
    }
  }, { root: null, rootMargin: '150px', threshold: 0.1 })

  observer.observe(el)
}

/**
 * Вотчер для отслеживания изменения поискового запроса
 *
 * Назначение:
 * - Реализует дебаунс для поиска чтобы избежать частых запросов к API
 * - Синхронизирует локальное значение поиска с фильтрами хранилища
 * - Инициирует загрузку данных с новым фильтром поиска
 *
 * Алгоритм:
 * 1. Очищает предыдущий таймер дебаунса
 * 2. Устанавливает новый таймер на DEBOUNCE_MS миллисекунд
 * 3. По истечении таймера обновляет фильтр в хранилище
 * 4. Вызывает загрузку данных с обновленным фильтром
 */
watch(localSearch, val => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    linksStore.filters.search = val
    void linksStore.fetchUsefulLinks()
  }, DEBOUNCE_MS)
})

/**
 * Вотчер для отслеживания изменения курсора пагинации
 *
 * Назначение:
 * - Автоматически настраивает наблюдатель при появлении нового курсора
 * - Обеспечивает непрерывную работу бесконечного скролла
 *
 * Логика:
 * - При появлении курсора устанавливает наблюдатель
 * - Позволяет продолжить подгрузку данных после обновления списка
 */
watch(() => linksStore.nextUsefulLinksCursor, () => {
  // если появился курсор —  установить observer
  setupObserver()
})

/**
 * Вотчер для отслеживания изменения активной вкладки
 *
 * Назначение:
 * - Управляет работой бесконечного скролла в зависимости от вкладки
 * - Отключает наблюдатель при переходе на вкладку "Избранные"
 * - Восстанавливает наблюдатель при возврате на вкладку "Все"
 *
 * Логика:
 * - На вкладке "Избранные" отключает observer для экономии ресурсов
 * - На вкладке "Все" настраивает observer для подгрузки данных
 */
watch(formTabValue, val => {
  if (val !== 0) disconnectObserver()
  else setupObserver()
})

/**
 * Хук монтирования компонента
 *
 * Выполняет:
 * 1. Загружает начальный список полезных ссылок
 * 2. Настраивает Intersection Observer для бесконечного скролла
 */
onMounted(async () => {
  await linksStore.fetchUsefulLinks()
  setupObserver()
})

/**
 * Хук размонтирования компонента
 *
 * Выполняет:
 * 1. Очищает таймер дебаунса для предотвращения утечек памяти
 * 2. Отключает Intersection Observer для освобождения ресурсов
 */
onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  disconnectObserver()
})
</script>

<template>
  <div class="links d-flex flex-column gap-2">
    <VCard class="links__header d-flex flex-column gap-4 px-6 py-4">
      <div class="d-flex align-center justify-space-between gap-4" :class="{ 'flex-column': xs }">
        <VTextField
          v-model="localSearch"
          variant="outlined"
          clearable
          class="flex-grow-0"
          :class="xs ? 'w-100' : 'w-25'"
          placeholder="Найти..."
          append-inner-icon="ri-search-line"
        />
        <VBtn
          v-if="permissionsStore.modules.links?.edit"
          :block="xs"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-settings-4-line"
          @click="router.push({ name: ERouteNames.LINKS_SETTINGS })"
        >
          Настройки
        </VBtn>
      </div>

      <VTabs v-model="formTabValue" class="" style="max-width: max-content">
        <div class="d-flex gap-2">
          <VTab :value="0">
            Все
          </VTab>
          <VTab :value="1">
            Избранные
          </VTab>
        </div>
      </VTabs>
    </VCard>

    <VCard class="links__body">
      <div v-if="!linksStore.links.length" class="pa-4 text-medium-emphasis">
        Данные отсутствуют
      </div>
      <VWindow v-else v-model="formTabValue" class="pa-4">
        <div v-if="formTabValue === 0" class="d-flex flex-column gap-1">
          <LinkCard
            v-for="link in linksStore.links"
            :key="link.id"
            :link="link"
            @toggle-favorite="onToggleFavorite"
          />
          <div
            v-if="linksStore.nextUsefulLinksCursor"
            ref="sentinel"
            style="width:1px; height:1px; opacity:0; pointer-events:none"
            aria-hidden="true"
          />
        </div>
        <div v-else class="d-flex flex-column gap-1">
          <LinkCard
            v-for="link in linksStore.favoritLinks"
            :key="link.id"
            :link="link"
            @toggle-favorite="onToggleFavorite"
          />
        </div>
      </VWindow>
    </VCard>
  </div>
</template>
